// src\app\model\User.ts

import { Company, CompanyInterface } from "./Company";

export type UserStatus = 'active' | 'terminated' | 'deactivated';


/**
 * UserInterface is an interface that conforms to the API response for users. This 
 * is used to safely cast data from the backend as well as sanitize data in the 
 * frontend before sending it to the backend
 */
export interface UserInterface {
    /** Attribute fields */
    id: number;
    email: string;
    firstName: string;
    lastName: string;
    currentToken: string;
    phoneNumber: string;
    status: UserStatus;
    permissions: {
        id: number | null;
        companyId: number;
        permission: string;
    }[];
    companies: CompanyInterface[];
}


/**
 * User represents a registered user in the transend system. It encapsulates user
 * interfaces to contain helpful information
 */
export class User {
    /** Attribute fields */
    private _id: number;
    private _email: string;
    private _firstName: string;
    private _lastName: string;
    private _currentToken: string;
    private _phoneNumber: string;
    private _status: UserStatus;
    private _permissions: PermissionEntry[];
    private _companies: Company[];

    /**
     * Construct the user from an interface
     */
    constructor(intf: UserInterface) {
        this._id = intf.id;
        this._email = intf.email;
        this._firstName = intf.firstName;
        this._lastName = intf.lastName;
        this._currentToken = intf.currentToken;
        this._phoneNumber = intf.phoneNumber; // Use the correct key from the API response
        this._status = intf.status;
        this._permissions = [];
        this._companies = [];

        // Add permissions
        for (const p of intf.permissions) {
            const permission = new PermissionEntry(p.id, p.companyId, p.permission);
            this._permissions.push(permission);
        }

        // Add companies
        for (const c of intf.companies) {
            const company = new Company(c.companyId, c.cifno, c.companyType, c.companyName, c.companyStatus);
            this._companies.push(company);
        }
    }

    /** Getters */
    public get id(): number {
        return this._id;
    }

    public get email(): string {
        return this._email;
    }

    public get firstName(): string {
        return this._firstName;
    }

    public get lastName(): string {
        return this._lastName;
    }

    public get phoneNumber(): string {
        return this._phoneNumber;
    }

    public get currentToken(): string {
        return this._currentToken;
    }

    public get permissions(): PermissionEntry[] {
        return this._permissions;
    }

    public get companies(): Company[] {
        return this._companies;
    }

    public get status(): UserStatus {
        return this._status;
    }

    /** Setters */
    public set id(id: number) {
        this._id = id;
    }

    public set email(email: string) {
        this._email = email;
    }

    public set phoneNumber(value: string) {
        this._phoneNumber = value;
    }

    public set firstName(firstName: string) {
        this._firstName = firstName;
    }

    public set lastName(lastName: string) {
        this._lastName = lastName;
    }

    public set currentToken(currentToken: string) {
        this._currentToken = currentToken;
    }


    public set permissions(permissions: PermissionEntry[]) {
        this._permissions = permissions;
    }

    public set companies(companies: Company[]) {
        this._companies = companies;
    }

    public set status(status: UserStatus) {
        this._status = status;
    }
}

/**
 * PermissionEntry represents a permission for the user
 */
export class PermissionEntry {
    /** Attribute fields */
    private _id: number | null;
    private _companyId: number;
    private _permission: string;

    /**
     * Construct a generic permission entry
     * 
     * @param id The id of the permission
     * @param companyId The id of the company
     * @param permission The permission string
     */
    constructor(id: number | null, companyId: number, permission: string) {
        this._id = id;
        this._companyId = companyId;
        this._permission = permission;
    }

    /** Getters */
    public get id(): number | null {
        return this._id;
    }

    public get companyId(): number {
        return this._companyId;
    }

    public get permission(): string {
        return this._permission;
    }

    /** Setters */
    public set id(id: number) {
        this._id = id;
    }

    public set companyId(companyId: number) {
        this._companyId = companyId
    }

    public set permission(permission: string) {
        this._permission = permission;
    }
}



/**
 * UserMapper is a service that maps between the User model and the UserInterface
 * interface that is used to interact with the backend API.
 */
export class UserMapper {

  /**
   * toDto takes a User and returns a UserInterface
   * 
   * @param model the User to convert
   * @returns a UserInterface
   */
  static toDto(model: User): UserInterface {
    return {
      id: model.id,
      email: model.email,
      firstName: model.firstName,
      lastName: model.lastName,
      currentToken: model.currentToken,
      phoneNumber: model.phoneNumber,
      status: model.status,
      permissions: model.permissions.map(p => ({
        id: p.id,
        companyId: p.companyId,
        permission: p.permission
      })),
      companies: model.companies.map(c => ({
        companyId: c.companyId,
        companyType: c.companyType,
        companyName: c.companyName,
        cifno: c.cifno,
        companyStatus: c.companyStatus
      }))
    };
  }

  /**
   * toModel takes a UserInterface and returns a User model
   * 
   * @param dto the UserInterface to convert
   * @returns a User model
   */
  static toModel(dto: UserInterface): User {
    return new User(dto);
  }
}